import {createApp} from "petite-vue";

import device from './services/device';
import radio from './services/radio';

import playerEmbed from './directives/embed/player-embed';
import playerRoll from './directives/embed/player-roll';
import lazyScripts from "./services/lazy-scripts";

let app = createApp({
    device: device.detect(),
    service: {
        ready: false,
    },
    banner: {
        mobile: false,
        tablet: false,
        desktop: false,
    },
    roll: {
        link: false,
        media: false,
        element: false,
        available: false,
        started: false,
        finished: false,
        middle: false,
        progress: 0,
        skip: {
            show: false,
            remain: 5,
            delay: 5,
        },
        vast: {
            client: false,
            tracker: false,
            source: null,
        },
        ready() {
            this.element.addEventListener('play', () => {
                // console.log("play");
                this.vast.tracker.trackImpression();
                this.vast.tracker.track('start');
                this.started = true;
            });

            this.element.addEventListener('timeupdate', () => {
                this.progress = parseInt(this.element.currentTime / this.element.duration * 100);
                if ( this.progress >= 50 && this.middle === false ) {
                    // console.log("middle");
                    this.vast.tracker.track('midpoint');
                    this.middle = true;
                }
                if ( this.element.currentTime >= this.skip.delay && this.skip.show === false ) {
                    this.skip.show = true;
                    return false;
                }
                if ( this.skip.show === false ) {
                    this.skip.remain = this.skip.delay - parseInt(this.element.currentTime);
                }
            });

            this.element.addEventListener('ended', () => {
                this.vast.tracker.complete();
                this.available = false;
                this.finished = true;
                this.player();
            });
        },
        finish() {
            // console.log("Skip");
            this.vast.tracker.track('skip');
            this.available = false;
            this.player();
        },
        click() {
            // console.log("Click");
            this.vast.tracker.click();
        },
        player() {
            radio.emit("play");
        }
    },
    postRoll: {
        link: false,
        media: false,
        element: false,
        available: false,
        started: false,
        finished: false,
        middle: false,
        progress: 0,
        skip: {
            show: false,
            remain: 5,
            delay: 5,
        },
        vast: {
            client: false,
            tracker: false,
            source: null,
        },
        ready() {
            radio.receive("play-post-roll", () => {
                this.play();
            });
            this.element.addEventListener('play', () => {
                this.vast.tracker.trackImpression();
                this.vast.tracker.track('start');
                this.started = true;
            });
            this.element.addEventListener('timeupdate', () => {
                this.progress = parseInt(this.element.currentTime / this.element.duration * 100);
                if ( this.progress >= 50 && this.middle === false ) {
                    // console.log("middle");
                    this.vast.tracker.track('midpoint');
                    this.middle = true;
                }
                if ( this.element.currentTime >= this.skip.delay && this.skip.show === false ) {
                    this.skip.show = true;
                    return false;
                }
                if ( this.skip.show === false ) {
                    this.skip.remain = this.skip.delay - parseInt(this.element.currentTime);
                }
            });
            this.element.addEventListener('ended', () => {
                this.vast.tracker.complete();
                this.available = false;
                this.finished = true;
            });
        },
        play() {
            if ( this.available && !(this.started || this.finished)) {
                this.element.play();
            }
        },
        finish() {
            this.vast.tracker.track('skip');
            this.available = false;
            this.player();
        },
        click() {
            this.vast.tracker.click();
        },
    },
    /**
     * The player
     */

    video: {
        link: null,
        source: {},
        title: null,
        poster: null,
        element: null,
        container: null,
        range: null,
        volume: null,
        preview: null,


        fullscreen: false,
        loaded: false,
        ended: false,
        buffered: 0,
        paused: true,
        playing: false,
        started: false,

        duration: {
            total: null,
            played: null,
            ratio: 0,
        },
        controls: {
            visible: false,
            timeout: 0,
        },
        toggleMuted() {
            this.element.muted = !this.element.muted;
            this.muted = this.element.muted;
            localStorage.setItem('bunny-muted', this.muted);
        },
        showControls() {
            clearTimeout(this.controls.timeout);
            this.container.dataset.active = "true";
            this.controls.visible = true;
        },
        hideControls( timeout = 3000) {
            clearTimeout(this.controls.timeout);
            this.controls.timeout = setTimeout(() => {
                this.container.dataset.active = "false";
                this.preview.parentNode.style.display = 'none';
                this.controls.visible = false;
            }, timeout);
        },
        playOverlay() {
            if (!('ontouchstart' in document.documentElement)) {
                return this.playPause();
            }
            if (!this.controls.visible) {
                this.showControls();
                this.hideControls();
            } else {
                this.hideControls(250);
            }
        },
        pause() {
            this.showControls();
            this.paused = true;
            this.element.pause();
        },
        playPause() {
            if (this.element.paused) {
                this.started = true;
                this.paused = false;
                this.ended = false;
                this.element.play();
                this.showControls();
                this.hideControls();
            } else {
                this.showControls();
                this.paused = true;
                this.element.pause();
            }
        },
        iOS() {
            return ['iPad Simulator','iPhone Simulator', 'iPod Simulator', 'iPad','iPhone', 'iPod'].includes(navigator.userAgent) || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
        },
        makeFullScreen() {

            // Ios specific
            if ( this.element.webkitEnterFullScreen && this.iOS()) {
                this.element.webkitEnterFullScreen();
                return false;
            }

            if ( this.fullscreen === false ) {
                const requestFullScreen = this.container.requestFullscreen || this.container.webkitEnterFullScreen || this.container.webkitRequestFullScreen || this.container.mozRequestFullScreen || this.container.msRequestFullScreen;
                requestFullScreen.call(this.container);
                this.fullscreen = true;
            } else {
                const cancelFullScreen = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
                cancelFullScreen.call(document);
                this.fullscreen = false;
            }
        },

        ready() {
            lazyScripts.loadHls().then(() => this.activate());
        },

        activate() {
            let instanceHls = null;
            let nativeHls = this.element.canPlayType('application/vnd.apple.mpegURL') || this.element.canPlayType('audio/mpegurl');

            /**
             * @see - https://github.com/video-dev/hls.js/blob/master/docs/API.md
             */
            if ( this.source.hls && this.source.hls.link && ( nativeHls || Hls.isSupported())) {
                // First check for native browser HLS support
                // If no native HLS support, check if HLS.js is supported
                if (!nativeHls) {
                    instanceHls = new Hls({
                        maxMaxBufferLength: 15,
                    });
                    instanceHls.loadSource(this.source.hls.link);
                    instanceHls.attachMedia(this.element);
                } else {
                    this.element.src = this.source.hls.link;
                    console.log(this.source.hls.link);
                }
            } else {
                this.element.src = this.source.mp4.link;
            }

            this.element.load();

            // console.log("Video - ready");

            this.element.addEventListener('play', () => {

                this.started = true;
            });

            this.element.addEventListener('loadeddata', () => {
                this.duration.total = this.time(this.element.duration);
                this.duration.played = this.time(this.element.currentTime);

                // console.log("Video - loaded");
                document.documentElement.style.setProperty('--bunny-volume', (this.element.volume*100) + "%");
                this.volume.value = this.element.volume;

                this.range.max = parseInt(this.element.duration);
                this.loaded = true;
            });

            this.element.addEventListener('timeupdate', () => {
                this.duration.played = this.time(this.element.currentTime);
                this.duration.ratio = parseInt(this.element.currentTime / this.element.duration * 100 );
                this.range.value = this.element.currentTime;
                document.documentElement.style.setProperty('--bunny-ratio', this.duration.ratio + "%");

            });

            this.element.addEventListener("ended", () => {
                this.paused = true;
                this.ended = true;
                // Play the post-roll if available
                radio.emit("play-post-roll");
                this.showControls();
            });

            radio.receive("play", () => {
                this.playPause();
            });

            this.containerEvents();
            this.volumeEvents();
            this.rangeEvents();
        },
        containerEvents() {
            this.container.dataset.active = "true";
            if (('ontouchstart' in document.documentElement)) {
                return false;
            }
            this.container.addEventListener('mousemove', () => {
                if ( !this.paused && this.started ) {
                    this.showControls();
                    this.hideControls();
                }
            });

            this.container.addEventListener('mouseleave', () => {
                if ( !this.paused && this.started ) {
                    this.hideControls();
                }
            });

        },
        volumeEvents() {
            this.volume.addEventListener('input', (event) => {
                // console.log("Volume input");
                this.element.volume = event.target.value;
                localStorage.setItem('bunny-volume', event.target.value);
                document.documentElement.style.setProperty('--bunny-volume', (this.element.volume*100) + "%");
            });
        },
        rangeEvents() {
            this.range.addEventListener('input', (event) => {
                this.element.currentTime = event.target.value;
                document.documentElement.style.setProperty('--bunny-ratio', parseInt(this.element.currentTime / this.element.duration * 100 ) + "%");
            });
        },
        time( number ) {
            number = parseInt(number);
            let hours   = Math.floor(number / 3600);
            let minutes = Math.floor((number - (hours * 3600)) / 60);
            let seconds = number - (hours * 3600) - (minutes * 60);

            hours = hours.toString().padStart(2, '0');
            minutes = minutes.toString().padStart(2, '0');
            seconds = seconds.toString().padStart(2, '0');

            if ( number >= 3600 ) {
                return hours + ':' + minutes + ':' + seconds;
            }

            return minutes +':' + seconds;

        }
    },

    /**
     * Global
     */

    mounted() {
        fetch('/service/embed.json')
            .then((r) => r.json())
            .then((r) => this.prepare(r.payload))
            .catch();
        device.event((data) => {
            this.device = data;
        });
    },
    prepare(json) {

        // PreRoll Vast
        if ( json.roll.before ) {
            this.roll.vast.source = json.roll.before;
        }
        // PostRoll Vast
        if ( json.roll.after ) {
            this.postRoll.vast.source = json.roll.after;
        }

        if ( json.video ) {
            this.video.home = json.video.home;
            this.video.link = json.video.link;
            this.video.title = json.video.title;
            this.video.poster = json.video.poster;
            this.video.preview = json.video.preview;
            this.video.source = json.video.source;
        }
        if ( json.banner ) {
            this.banner = json.banner;
        }
        this.service.ready = true;
    },
    play() {
        if ( this.roll.vast.source ) {
            lazyScripts.loadVast().then(() => {
                this.roll.vast.client = new VAST.VASTClient();
                this.roll.vast.client.get( this.roll.vast.source ).then(res => {
                    if ( res.hasOwnProperty('ads') === false || res.ads.length === 0 ) {
                        return false;
                    }
                    this.roll.vast.tracker = new VAST.VASTTracker(this.roll.vast.client, res.ads[0], res.ads[0].creatives[0]);
                    this.roll.vast.tracker.load();
                    this.roll.vast.tracker.trackImpression();

                    this.roll.media = res.ads[0].creatives[0].mediaFiles[0].fileURL;
                    this.roll.link = res.ads[0].creatives[0].videoClickThroughURLTemplate.url;

                    this.roll.available = true;

                    setTimeout(() => {
                        this.roll.element.play();
                        this.video.playPause();
                        this.video.playPause();
                    }, 250);
                });

                this.postRoll.vast.client = new VAST.VASTClient();
                this.postRoll.vast.client.get( this.roll.vast.source ).then(res => {
                    if ( res.hasOwnProperty('ads') === false || res.ads.length === 0 ) {
                        return false;
                    }
                    this.postRoll.vast.tracker = new VAST.VASTTracker(this.postRoll.vast.client, res.ads[0], res.ads[0].creatives[0]);
                    this.postRoll.vast.tracker.load();
                    this.postRoll.vast.tracker.trackImpression();
                    this.postRoll.media = res.ads[0].creatives[0].mediaFiles[0].fileURL;
                    this.postRoll.link = res.ads[0].creatives[0].videoClickThroughURLTemplate.url;
                    this.postRoll.available = true;
                });
            });
        } else {
            this.video.playPause();
        }
    },
});

app.directive('player-roll', playerRoll);
app.directive('player-embed', playerEmbed);
app.mount();
